import { useEffect, useRef, useState } from "react"

import { Alert, App, Button, Flex, Layout, Modal, Space, Spin, Tag, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"
import ReactPlayer from "react-player"
import { shallowEqual, useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router"
import { useLocation, useNavigate } from "react-router-dom"

import ButtonComponent from "../../../Components/Button"
import ConnectPaymentProcessorForm from "../../../Components/Forms/ConnectPaymentProcessorForm"
import Tour from "../../../Components/Tour"
import { stepsTourConnectPaymentProcessor } from "../../../Components/Tour/stepsTourConnectPaymentProcessot"
import { RoutesPaths } from "../../../constants/user-routes"
import Icon from "../../../Icon"
import { modalConfig } from "../../../theme"
import { getJourneyState } from "../../../Utils/onboarding/getJourneyState"
import {
  getBusinessPathByStepId,
  getOnboardingPathByStepId
} from "../../../Utils/onboarding/onboardingFlow"
import {
  CHOICE_FLOWS,
  choiceIncludes,
  FEATURES,
  hasOnboardingProgress,
  landingPathAfterOnboarding,
  resolveProductChoice,
  supportsRecover
} from "../../../Utils/onboarding/productChoice"
import {
  getApplicableSteps,
  getSkippedStepKeys,
  nextApplicableStepId
} from "../../../Utils/onboarding/stepSequence"
import { highAccessRoles } from "../../../Utils/permission"
import utils, {
  createAccountStepPath,
  handleViewTutorial,
  manageEnrollmentStepPath,
  onboardingStepPath,
  paymentProcessorDescription,
  paymentProcessorMap,
  useQuery
} from "../../../Utils/utils"
import { BA_STEPS } from "../../GettingStartedPage/utils/businessSteps"
import { OB_STEPS } from "../../GettingStartedPage/utils/onboardingSteps"
import IntegrationCard from "../../IntegrationsPage/components/Card"
import ConnectNextcommerce from "../../IntegrationsPage/Nextcommerce/ConnectNextcommerce"
import useNextcommerceDisconnect from "../../IntegrationsPage/Nextcommerce/hooks/useNextcommerveDisconnect"
import { updateProfile } from "../../LoginPage/redux/auth.actions"
import { changeNextCreateAccountStep, userSetProp } from "../../User/redux/user.actions"
import { userActions } from "../../User/redux/user.slice"
import {
  changeOnboardingStep,
  connectPaymentProcessor,
  disconnectPaymentProcessor,
  getBusinessAccounts,
  getPaymentProcessors,
  getUserInfo,
  userSave
} from "../../User/redux/user.thunks"
import { finalizeOnboarding, updateCompletedStep } from "../../User/services"
import MobileCtaButtons from "./components/MobileCtaButtons"

import "./styles.less"

const { Title, Text } = Typography
const MOST_POPULAR = new Set(["stripe", "shopify", "manual_refunds"])

const FEATURE_LABELS = { [FEATURES.PREVENT]: "Prevent", [FEATURES.RECOVER]: "Recover" }

// Which products a processor serves, for the badges shown when both were picked (OB-6-08).
// The no-integration card carries none.
const productBadges = (processor) => {
  if (processor.service_name === "manual_refunds") return null

  const products = Array.isArray(processor.available_for)
    ? processor.available_for
    : [FEATURES.PREVENT, ...(supportsRecover(processor) ? [FEATURES.RECOVER] : [])]

  return products.map((key) => FEATURE_LABELS[key]).filter(Boolean)
}

const NOTICE_BY_CHOICE = {
  prevent:
    "Connect a payment gateway or billing system for automatic chargeback prevention. If it’s not visible, select Other instead.",
  recover:
    "Connect the payment processor your disputes come from. Each new dispute and its transaction data is retrieved from there to build your response.",
  both: "Connect a payment gateway or billing system for automatic chargeback prevention and dispute fighting, plus alert-to-transaction matching."
}

const splitProcessors = (processors = []) => {
  const mostPopular = []
  const moreIntegrations = []

  processors.forEach((p) => {
    if (MOST_POPULAR.has(p.service_name)) {
      mostPopular.push(p)
    } else {
      moreIntegrations.push(p)
    }
  })

  return { mostPopular, moreIntegrations }
}

const OnboardingPaymentConnectPage = (props) => {
  const { step, maxStep } = props

  const dispatch = useDispatch()
  const { enrollmentId } = useParams()
  const location = useLocation()
  const query = useQuery(location.search)
  const isOAuthReturn = query.get("oauth_return") === "1"
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { notification, modal } = App.useApp()

  const handleNavigate = () => {
    if (isNewBusiness) {
      navigate(createAccountStepPath[7])
      return
    }
    navigate(onboardingStepPath[2])
  }

  const [openTour, setOpenTour] = useState(false)

  // show modal
  const [visible, setVisible] = useState(false)
  // video or connectForm
  const [modalType, setModalType] = useState("")

  const [connectPayment, setConnectPayment] = useState(null)
  const [customConnectServiceName, setCustomConnectServiceName] = useState(null)
  const [continueAfterConnect, setContinueAfterConnect] = useState(false)

  const { fetching, userInfo, isAdmin } = useSelector((s) => s.auth)
  const isMobile = useSelector((s) => s.nav.isMobile)
  const {
    fetching: userFetching,
    businessAccountFetching,
    paymentProcessors,
    businessAccount,
    nextCreateAccountStep,
    saveResult,
    isOneProcessorConnected,
    businessAccountsWithRole: businessAccounts
  } = useSelector((s) => s.user, shallowEqual)
  const userState = useSelector((s) => s.user)

  const hasHighAccessRole = businessAccounts?.some(({ role }) =>
    highAccessRoles?.includes(role?.id)
  )

  const isNewBusiness = location.pathname?.includes("/create-account")

  // The product choice decides which processors are offered, what the notice says, whether the
  // connection can be deferred, and what comes after this step (OB-6-04..08, OB-6-15, OB-6-16).
  const choiceFlow = isNewBusiness ? CHOICE_FLOWS.BUSINESS : CHOICE_FLOWS.ONBOARDING
  const productChoice = resolveProductChoice({
    flow: choiceFlow,
    hasProgress: hasOnboardingProgress({ flow: choiceFlow, userInfo, nextCreateAccountStep })
  })
  const preventChosen = choiceIncludes(productChoice, FEATURES.PREVENT)
  const recoverChosen = choiceIncludes(productChoice, FEATURES.RECOVER)
  const recoverOnly = recoverChosen && !preventChosen
  const bothChosen = preventChosen && recoverChosen && productChoice !== null
  const noticeText = recoverOnly
    ? NOTICE_BY_CHOICE.recover
    : bothChosen
      ? NOTICE_BY_CHOICE.both
      : NOTICE_BY_CHOICE.prevent

  const journey = getJourneyState({ userInfo, userState, pathname: location.pathname })

  const currentBusinessAccountId = userInfo?.["current_business_account_id"]
  let businessId =
    businessAccount?.id === currentBusinessAccountId
      ? businessAccount?.id
      : currentBusinessAccountId
  let selectedPayment = paymentProcessors
    ?.filter((pp) => pp?.["is_connected"])
    ?.map((pp) => pp?.["service_name"])
  let selectedNotManual = selectedPayment.length && !selectedPayment?.includes("manual_refunds")
  const normalizedProcessors = paymentProcessors?.map((el) => ({
    ...el,
    is_connected: el.is_connected,
    title: el.title || paymentProcessorMap?.[el.service_name] || el.service_name,
    isDisabled: el.isDisabled || (el.service_name === "manual_refunds" && selectedNotManual)
  }))
  // A Recover-only setup lists only what Recover can read disputes from: no "Other", no
  // Prevent-only processors (OB-6-06, OB-6-12). Whether Recover has something to work with is
  // judged the same way, so a leftover "Other" never counts as connected here.
  const hasRecoverProcessor = (paymentProcessors || []).some(
    (p) => p?.is_connected && supportsRecover(p)
  )
  const offeredProcessors = recoverOnly
    ? normalizedProcessors?.filter(supportsRecover)
    : normalizedProcessors
  const { mostPopular, moreIntegrations } = splitProcessors(offeredProcessors)
  const featureBadgesFor = (payment) => (bothChosen ? productBadges(payment) : null)

  const notificationConfig = {
    closeIcon: <Text>{t("Dismiss")}</Text>,
    style: { minWidth: "max-content" }
  }

  const handleCloseModal = () => {
    setVisible(false)
    setModalType("")
    if (saveResult?.["connect_error"]) dispatch(userActions.userSaveResultReset())
  }

  useEffect(() => {
    if (userInfo && businessId) {
      dispatch(
        getPaymentProcessors({
          merchant_id: businessId,
          // `?oauth_return=1` survives the round trip through an external OAuth
          // provider (Shopify) and back - see the mount effect below, which reads it to
          // tell "just connected something externally" apart from a plain revisit.
          return_url: process.env.REACT_APP_BASE_URI + location.pathname + "?oauth_return=1"
        })
      )
    }
    document.title = "Payment processor connect - Chargeback"
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [fetching, businessId])

  useEffect(() => {
    if (!connectPayment) {
      if (userInfo?.["new_onboarding_finished"]) {
        if (enrollmentId) {
        } else {
          dispatch(changeNextCreateAccountStep(2))
        }
      } else if (!isOAuthReturn) {
        // Syncs `next_step` back down to this page's own step when the user is
        // revisiting it (e.g. via "Back") after already having advanced further -
        // gated on `isOAuthReturn` because Shopify's OAuth round trip also lands back
        // here with `connectPayment` freshly null on a full page reload, and in that
        // case being "ahead" is the user's real, just-completed progress, not something
        // to roll back. The backend also rejects a backward next_step write outright
        // now, so this can't corrupt state even if the two ever disagree.
        if (userInfo?.next_step - 2 > step) {
          dispatch(changeOnboardingStep({ next_step: step + 2 }))

          updateCompletedStep({
            step: step - 1,
            business_account_id: businessId
          })
        }
      }
    }
  }, [connectPayment])

  useEffect(() => {
    if ((saveResult === "connected" || saveResult === "disconnected") && connectPayment) {
      let namePayment =
        paymentProcessorMap?.[connectPayment.service_name] ||
        utils.toUpperLatter(connectPayment.service_name)
      let message = null
      let description = null
      if (saveResult === "connected") {
        if (connectPayment.service_name === "manual_refunds") {
          message = t("Other selected")
          description = t("Other was selected successfully")
        } else {
          message = t("Payment processor connected")
          description = t("_namePayment_ was connected successfully.", {
            namePayment
          })
        }
      } else if (saveResult === "disconnected") {
        if (connectPayment.service_name === "manual_refunds") {
          message = t("Other deselected")
          description = t("Other was deselected successfully")
        } else {
          message = t("Payment processor disconnected")
          description = t("_namePayment_ was disconnected successfully.", {
            namePayment
          })
        }

        if (
          !isOneProcessorConnected &&
          connectPayment.service_name !== "manual_refunds" &&
          enrollmentId
        ) {
          dispatch(
            getPaymentProcessors({
              merchant_id: businessId,
              return_url: process.env.REACT_APP_BASE_URI + location.pathname
            })
          )
        }
      }
      notification.success({
        message,
        description,
        ...notificationConfig
      })
      if (
        saveResult === "connected" &&
        continueAfterConnect &&
        connectPayment.service_name === "manual_refunds"
      ) {
        setContinueAfterConnect(false)
        handleSubmit()
      }
      handleCloseModal()
      dispatch(userActions.userSaveResultReset())
    }
  }, [saveResult, isOneProcessorConnected, continueAfterConnect])

  const handleContinue = () => {
    const manual = mostPopular.find((i) => i.service_name === "manual_refunds")
    setContinueAfterConnect(true)
    handleClick(manual)
  }

  // "Other" is the no-integration fallback: the moment a real processor is connected it no longer
  // applies and is deselected on the backend too, not merely greyed out (OB-6-13). The reducer
  // already hides it locally after an in-app connect, so what matters is whether it was selected
  // at any point on this page; an OAuth return lands with both flags on. Once per mount, so a
  // refused deselect cannot loop.
  const otherSelection = useRef({ wasSelected: false, deselected: false })

  useEffect(() => {
    const other = paymentProcessors?.find((p) => p.service_name === "manual_refunds")

    if (!other) return

    if (other.is_connected) otherSelection.current.wasSelected = true

    const realConnected = paymentProcessors.some(
      (p) => p.is_connected && p.service_name !== "manual_refunds"
    )

    if (
      !realConnected ||
      !otherSelection.current.wasSelected ||
      otherSelection.current.deselected
    ) {
      return
    }

    otherSelection.current.deselected = true
    setConnectPayment(other)
    dispatch(
      disconnectPaymentProcessor({
        payment_processor_id: other.id,
        service_name: other.service_name
      })
    )
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [paymentProcessors])

  // The flow is over for this business: the last applicable step was this one (a Recover-only
  // additional business has nothing after Connect integration). Finish it on the backend, refresh
  // the account state, and land where the choice says (OB-10-04..07, OB-10-08).
  async function finishBusinessFlow() {
    const response = await finalizeOnboarding()

    if (!response?.ok) {
      notification.error({
        message: t("Could not finish setting up the business"),
        description: response?.data?.message?.detail || response?.data?.detail,
        ...notificationConfig
      })

      return
    }

    const landing = landingPathAfterOnboarding(productChoice)

    if (response.data) dispatch(updateProfile(response.data))

    // Clearing the step pointer drops these routes' auth and their redirect fires; point it at
    // the landing first so the two agree.
    dispatch(userSetProp({ key: "redirectAfterCreateAccount", value: landing }))
    dispatch(changeNextCreateAccountStep(null))
    dispatch(getBusinessAccounts())
    dispatch(getUserInfo())
    navigate(landing, { replace: true })
  }

  // Everything that happens once this step is satisfied: record it, move the pointer and go to
  // whatever the chosen products make next. Shared by Continue and the Recover-only skip.
  async function advanceAfterIntegration() {
    if (nextCreateAccountStep) {
      await updateCompletedStep({ step, business_account_id: businessId })

      const skippedIds = getSkippedStepKeys(productChoice).map((key) => BA_STEPS[key])
      const nextBaStep =
        journey.steps.find((s) => s.step > BA_STEPS.INTEGRATION && !skippedIds.includes(s.step))
          ?.step ?? null

      if (nextBaStep === null) {
        await finishBusinessFlow()

        return
      }

      dispatch(changeNextCreateAccountStep(nextBaStep))

      if (userInfo?.is_created_by_invitation) {
        // An invited user's own first business also moves the user-level pointer; the two
        // step maps are offset by the email and features steps.
        const nextObStep = nextBaStep + 2
        dispatch(updateProfile({ next_step: nextObStep }))
        dispatch(userSave({ next_step: nextObStep }))
      }

      navigate(getBusinessPathByStepId(nextBaStep))

      return
    }

    if (enrollmentId) {
      dispatch(userSetProp({ key: "nextManageEnrollmentStep", value: 2 }))
      navigate(manageEnrollmentStepPath(enrollmentId, isAdmin)[2], { replace: true })

      return
    }

    // Prevent goes on to Choose enrollment; without it the next screen is the first billing
    // step, since enrollment and alerts do not apply (OB-X-01).
    const nextObStep = nextApplicableStepId({
      stepId: OB_STEPS.INTEGRATION + 1,
      steps: getApplicableSteps({ choice: productChoice }),
      stepMap: OB_STEPS
    })

    // Persist the pointer whenever it is behind, not only when it sits exactly on this step: a
    // merchant whose Company profile save was an update arrives here with the pointer still on
    // that step, and the old equality check left it there for the rest of the flow.
    if ((userInfo?.next_step ?? OB_STEPS.EMAIL) < nextObStep) {
      dispatch(changeOnboardingStep({ next_step: nextObStep }))
    }

    await updateCompletedStep({ step, business_account_id: businessId })
    dispatch(updateProfile({ next_step: nextObStep }))
    navigate(getOnboardingPathByStepId(nextObStep))
  }

  async function handleSubmit() {
    // Recover needs a processor it can read disputes from; "Other" or a Prevent-only processor
    // does not count for it (OB-6-16).
    const hasUsableProcessor = recoverOnly ? hasRecoverProcessor : selectedPayment?.length

    if (hasUsableProcessor) {
      await advanceAfterIntegration()

      return
    }

    if (recoverOnly) {
      // No "Other" for Recover: either a supported processor is connected or the connection is
      // deferred to Configure, where it is required (OB-6-15, OB-6-18, OB-6-M06..M08).
      modal.confirm({
        ...modalConfig("exclamation", 442),
        className: `ob-clear-bg cdrn ${isMobile && "mobile"}`,
        okButtonProps: { size: "large", style: { width: "auto" } },
        cancelButtonProps: { size: "large", style: { width: "auto" } },
        centered: !isMobile,
        title: t("No integration connected"),
        content: (
          <Text type={"secondary"}>
            {t(
              "Recover needs a connected payment processor to receive and fight your disputes. You can connect one now, or later from Configure, where it is required before Recover can start."
            )}
          </Text>
        ),
        okText: t("I'll connect later"),
        cancelText: t("Cancel"),
        onOk() {
          return advanceAfterIntegration()
        }
      })

      return
    }

    modal.warning({
      ...modalConfig("exclamation", 442),
      className: `ob-clear-bg cdrn ${isMobile && "mobile"}`,
      okButtonProps: { size: "large", style: { width: "auto" } },
      cancelButtonProps: { size: "large", style: { width: "auto" } },
      centered: !isMobile,
      okCancel: true,
      onOk() {
        handleContinue()
      },

      title: t("No integration connected"),
      content: (
        <Flex vertical gap={26}>
          <div>
            <Text type={"secondary"}>
              While not required for alerts, we <Text bold>recommend</Text> connecting your gateway
              or billing system to automate alert to transaction matching and auto-refunds.
            </Text>
          </div>
          <div>
            <Text type={"secondary"}>
              If you continue without selecting a processor, <Text bold>“Other”</Text> will be
              enabled by default. You can always connect integration later.
            </Text>
          </div>
        </Flex>
      ),
      okText: t("Continue"),
      cancelText: t("Cancel")
    })
  }

  const { disconnectNext } = useNextcommerceDisconnect()

  const disconnectPayment = (payment) => {
    const isOther = payment.service_name === "manual_refunds"

    modal.confirm({
      ...modalConfig("exclamation"),
      className: "ob-clear-bg cdrn",
      centered: !isMobile,

      title: isOther ? t("Deselect Other") : t("Disconnect payment processor"),
      content: isOther
        ? t("Are you sure you want to deselect Other?")
        : t("Are you sure you want to disconnect your payment processor?"),

      cancelText: t("Cancel"),
      okText: isOther ? t("Deselect") : t("Disconnect"),
      cancelButtonProps: { width: 70, height: 40 },
      okButtonProps: {
        width: 75,
        height: 40
      },
      onOk() {
        if (payment.service_name === "next") {
          disconnectNext(businessId)
          return
        }
        dispatch(
          disconnectPaymentProcessor({
            payment_processor_id: payment.id,
            service_name: payment.service_name
          })
        )
      },
      onCancel() {
        console.log("Cancel")
      }
    })
  }

  const handleConnectPaymentWithForm = (data) => {
    if (!connectPayment) return
    dispatch(
      connectPaymentProcessor({
        service_name: connectPayment.service_name,
        merchant_id: connectPayment.merchant_id,
        ...data
      })
    )
  }

  const handleClick = (payment) => {
    if (payment.is_connected) {
      setConnectPayment(payment)
      disconnectPayment(payment)
    } else if (payment?.service_name === "next") {
      setCustomConnectServiceName(payment.service_name)
    } else {
      if (!payment.link && payment?.["list_of_required_fields_for_tokens"].length) {
        setConnectPayment(payment)
        if (payment?.service_name === "adyen") {
          setModalType("connectForm")
          setVisible(true)
        } else {
          setOpenTour(true)
        }
      } else {
        if (payment.service_name === "manual_refunds") {
          setConnectPayment(payment)
          dispatch(
            connectPaymentProcessor({
              service_name: payment.service_name,
              payment_processor_id: payment.id
            })
          )
        } else {
          if (payment.link) utils.goLink(payment.link)
        }
      }
    }
  }

  const handleCloseTour = (closedBy) => {
    if (closedBy === "mask") {
      setOpenTour(false)
      return
    }
    setOpenTour(false)
    setModalType("connectForm")
    setVisible(true)
  }

  const handleOpenTour = () => {
    setOpenTour(true)
    setModalType(null)
    setVisible(false)
  }

  const handleBack = () => {
    if (isNewBusiness) {
      return navigate(createAccountStepPath[BA_STEPS.COMPANY])
    }
    navigate(onboardingStepPath[OB_STEPS.COMPANY])
  }

  const TutorialButton = ({ serviceName, isMobile }) => {
    const isAdyen = serviceName === "adyen"

    return (
      <>
        {!isMobile ? (
          <ButtonComponent
            type="text"
            size="large"
            className="flex center"
            style={isMobile ? { width: "100%" } : { marginRight: "auto" }}
            icon={isAdyen ? null : <Icon type="arrow_left" />}
            onClick={isAdyen ? handleViewTutorial : handleOpenTour}
          >
            {t("View tutorial")}
          </ButtonComponent>
        ) : (
          <ButtonComponent
            htmlType="button"
            size="large"
            className="flex center"
            style={isMobile ? { width: "100%" } : { marginRight: "auto" }}
            onClick={isAdyen ? handleViewTutorial : handleOpenTour}
          >
            {t("View tutorial")}
          </ButtonComponent>
        )}
      </>
    )
  }

  const modalBlocks = {
    connectForm: {
      classNames: {
        content: cls("clear-bg full-width-header", {
          "bg-center": modalType === "success",
          "payment-connect": modalType === "connectForm",
          recurly: connectPayment?.["service_name"] === "recurly"
        }),
        header: cls({
          "payment-connect": modalType === "connectForm",
          [`${connectPayment?.["service_name"]}`]: modalType === "connectForm"
        })
      },
      title: (
        <Space direction="vertical">
          <Text className="payment-connect-title">
            {t("_namePayment_ integration", {
              namePayment: paymentProcessorMap[connectPayment?.["service_name"]]
            })}
          </Text>
          <Text className="payment-connect-sub_title">
            {t(
              paymentProcessorDescription[connectPayment?.["service_name"]] ??
                "Connect your _namePayment_ account to fully automate your _namePayment_ disputes.",
              {
                namePayment: paymentProcessorMap[connectPayment?.["service_name"]]
              }
            )}
          </Text>
        </Space>
      ),
      component: (
        <ConnectPaymentProcessorForm
          fields={connectPayment?.["list_of_required_fields_for_tokens"]?.filter(
            (el) => el !== "merchant_id"
          )}
          onClose={handleCloseModal}
          service_name={connectPayment?.service_name}
          onSubmit={handleConnectPaymentWithForm}
          disabled={businessAccountFetching}
          error={saveResult?.["connect_error"]}
          extraBtn={
            <TutorialButton serviceName={connectPayment?.service_name} isMobile={isMobile} />
          }
          isMobile={isMobile}
        />
      )
    },
    video: {
      classNames: { wrapper: "video-modal", content: "clear-bg" },
      title: null,
      component: (
        <ReactPlayer
          controls={true}
          width={"100%"}
          height={"100%"}
          playing={true}
          url={"https://www.youtube.com/watch?v=OftbjjbL0tc"}
        />
      )
    }
  }

  return (
    <Layout>
      <Spin
        wrapperClassName="payment-connect-spinner"
        size="large"
        spinning={fetching || businessAccountFetching || userFetching}
      >
        <div className="on-boarding-content">
          <div className="content-main">
            <Flex
              vertical
              align={"center"}
              style={{
                width: "100%",
                maxWidth: 1040
              }}
            >
              <div className="on-boarding-content__header">
                {!isMobile && (
                  <Button
                    className="back-btn"
                    type="text"
                    icon={<Icon type="arrow_left" />}
                    onClick={handleBack}
                  >
                    Back
                  </Button>
                )}
                <h2 className="card-title">{t("Add an integration")}</h2>
                <h4 className="card-subtitle-signup">
                  {t("You can always connect or switch integration later.")}
                </h4>
              </div>
              <Alert
                className="primary notice-alert"
                icon={<Icon type="warning_full" />}
                showIcon
                message={<Text strong>{t("Important notice")}</Text>}
                description={
                  <Text>
                    {t(
                      enrollmentId
                        ? "Please be aware that connecting or disconnecting an integration will affect the entire business account, not just the selected enrollment."
                        : noticeText
                    )}
                  </Text>
                }
                style={{
                  margin: !isMobile ? "32px 0 24px" : "16px 0 24px",
                  width: "100%"
                }}
              />
              {/* A Recover-only list is just the processors Recover supports: no groups (OB-6-07). */}
              {recoverOnly ? null : (
                <Flex justify="start" style={{ width: "100%", marginBottom: 14 }}>
                  <Tag className="integration_type integration_type--blue">Most popular</Tag>
                </Flex>
              )}

              <Flex
                gap={16}
                wrap="wrap"
                justify="center"
                align="stretch"
                className="responsive-flex"
                style={{
                  marginBottom: 32,
                  maxWidth: "994px",
                  alignSelf: "flex-start"
                }}
              >
                {mostPopular.map((payment) => {
                  return (
                    <IntegrationCard
                      key={payment.id}
                      data={payment}
                      style={{ width: 320 }}
                      onClick={handleClick}
                      featureBadges={featureBadgesFor(payment)}
                      disabled={
                        !isAdmin &&
                        !hasHighAccessRole &&
                        location.pathname.includes(RoutesPaths.configure.enrollment)
                      }
                    />
                  )
                })}
              </Flex>
              {moreIntegrations.length ? (
                <Flex justify="start" style={{ width: "100%", marginBottom: 16 }}>
                  <Tag className="step-status integration_type--green">More integrations</Tag>
                </Flex>
              ) : null}
              <Flex
                gap={16}
                wrap="wrap"
                justify="center"
                align="stretch"
                className="responsive-flex"
                style={{
                  marginBottom: isMobile ? 140 : 60,
                  maxWidth: "994px",
                  alignSelf: "flex-start"
                }}
              >
                {moreIntegrations.map((payment) => {
                  return (
                    <IntegrationCard
                      key={payment.id}
                      data={payment}
                      style={{ width: 320 }}
                      onClick={handleClick}
                      featureBadges={featureBadgesFor(payment)}
                      disabled={
                        !isAdmin &&
                        !hasHighAccessRole &&
                        location.pathname.includes(RoutesPaths.configure.enrollment)
                      }
                    />
                  )
                })}
              </Flex>
              <div className="pp-sticky-cta">
                <MobileCtaButtons
                  isMobile={isMobile}
                  onSaveForLater={handleNavigate}
                  onBack={handleBack}
                />
                <div className="pp-cta-row">
                  {/* Recover only: the connection can wait for Configure; the button goes once a
                      processor Recover can use is connected (OB-6-16). */}
                  {recoverOnly && !hasRecoverProcessor ? (
                    <ButtonComponent
                      size="large"
                      loading={userFetching}
                      onClick={advanceAfterIntegration}
                      className="pp-cta"
                    >
                      {t("I'll connect later")}
                    </ButtonComponent>
                  ) : null}
                  <ButtonComponent
                    type="primary"
                    size="large"
                    loading={userFetching}
                    onClick={handleSubmit}
                    className="pp-cta"
                  >
                    {t("Continue")}
                  </ButtonComponent>
                </div>
              </div>
            </Flex>
          </div>
        </div>
      </Spin>

      <Tour
        open={openTour}
        className={cls("connect-payment-tour", "tour-with-skip")}
        steps={stepsTourConnectPaymentProcessor()[connectPayment?.service_name]}
        onClose={handleCloseTour}
        key={connectPayment?.service_name}
        isClosableOnMaskClick={true}
        indicatorsRender={() => (
          <ButtonComponent size="large" onClick={handleCloseTour}>
            Skip tutorial
          </ButtonComponent>
        )}
      />

      <Modal
        {...(isMobile ? { transitionName: "" } : null)}
        open={visible}
        centered={!isMobile}
        width={442}
        title={modalBlocks[modalType]?.title}
        footer={null}
        closable="true"
        closeIcon={<Icon type="close_modal" />}
        onCancel={handleCloseModal}
        destroyOnClose
        classNames={modalBlocks[modalType]?.classNames}
      >
        <Spin size="large" spinning={fetching} wrapperClassName="visible">
          {modalBlocks[modalType]?.component}
        </Spin>
      </Modal>
      {customConnectServiceName === "next" && (
        <ConnectNextcommerce
          onFinish={() => setCustomConnectServiceName(null)}
          isMobile={isMobile}
        />
      )}
    </Layout>
  )
}

export default OnboardingPaymentConnectPage
